import { useAllHostSessions } from '../../../utils/queries/analytics';
import { localDate } from 'utils/misc';
import { get, round, map, sumBy, filter, cloneDeep, orderBy } from 'lodash-es';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import './style.css';
import '../gridStyle.css';
import { Table, Spin, Typography, Popover, Button } from 'antd';
import { useEffect, useState } from 'react';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { InfoCircleOutlined } from '@ant-design/icons';
import { find } from 'lodash';
import { Link } from 'react-router-dom';
import ChargerSessionModal from 'components/Modals/ChargerSessionModal';

const { Text } = Typography;

const ChargingSessionsTable = ({
  chargingSessionsParams,
  showDateRange = false,
  chargersDetails,
  setInvoicePopup = null,
}) => {
  const limit = 10;
  const [count, setCount] = useState(0);
  const [selectedSession, setSelectedSession] = useState('');
  const [openChargerSessionModal, setOpenChargerSessionModal] = useState(false);
  const toggleChargerSessionModal = () =>
    setOpenChargerSessionModal(!openChargerSessionModal);
  const getSuffixPrefixOfComission = (commissionType, value) => {
    let response = '';

    if (commissionType === 'PERCENTAGE') {
      response = `${value}%`;
    } else if (commissionType === 'FIXED') {
      response = `₹${value}`;
    }

    return response;
  };

  const [isVisibilityPopoverOpen, setVisibilityPopoverOpen] = useState(false);
  const [requestParam, setRequestParam] = useState(null);
  const [sessionData, setSessionData] = useState([]);
  const [sortObject, setSortObject] = useState({
    columnKey: 'START_TIME',
    order: 'ascend',
  });
  const [filterObject, setFilterObject] = useState(null);
  const { data, isLoading, refetch } = useAllHostSessions(requestParam);

  useEffect(() => {
    setSessionData(data);
    setCount(data?.length);
  }, [data]);

  useEffect(() => {
    if (
      chargingSessionsParams?.chargers?.length === 0 &&
      chargingSessionsParams?.states?.length === 0 &&
      chargingSessionsParams?.startDate === '' &&
      chargingSessionsParams?.endDate === ''
    )
      return;
    else {
      setRequestParam(chargingSessionsParams);
      setTimeout(() => {
        refetch();
      }, 100);
    }
  }, [chargingSessionsParams, refetch]);

  const getPopoverOfCommissionDetails = (commissionType, commission) => {
    const content = (
      <div className="commission-item">
        {`Commission: ${getSuffixPrefixOfComission(
          commissionType,
          commission,
        )} per Unit`}
      </div>
    );

    return (
      <Popover content={content} trigger="hover">
        <span style={{ margin: 3, position: 'relative', top: -3 }}>
          <InfoCircleOutlined />
        </span>
      </Popover>
    );
  };

  const columns = [
    {
      title: 'Charger Id',
      dataIndex: 'CPID',
      visibility: true,
      render: (text, session) => (
        <Link to={`/admin/chargers/${session.CPID}`} className="chargerLink">
          <span className="mb-0 text-sm">{get(session, 'CPID')}</span>
        </Link>
      ),
      key: 'CPID',
      width: 100,
    },
    {
      title: 'Transaction Id',
      dataIndex: 'TRANSACTIONID',
      visibility: true,
      render: (text, session) => (
        <span
          style={{ fontWeight: 600, color: '#317ffe', cursor: 'pointer' }}
          onClick={() => {
            setSelectedSession(session?.TRANSACTIONID);
            toggleChargerSessionModal();
          }}
        >
          {session?.TRANSACTIONID}
        </span>
      ),
      key: 'TRANSACTIONID',
      width: 100,
    },
    {
      title: 'Started at',
      dataIndex: 'START_TIME',
      visibility: true,
      render: (text, session) => <>{localDate(session?.START_TIME)}</>,
      key: 'START_TIME',
      sorter: {
        compare: (a, b) => new Date(a.START_TIME) - new Date(b.START_TIME),
        multiple: 1,
      },
      defaultSortOrder: 'descend',
      width: 100,
    },
    {
      title: 'Ended at',
      dataIndex: 'END_TIME',
      visibility: true,
      render: (text, session) => <>{localDate(session?.END_TIME)}</>,
      key: 'END_TIME',
      sorter: (a, b) => new Date(a.END_TIME) - new Date(b.END_TIME),
      // defaultSortOrder: '',
      width: 100,
    },
    {
      title: 'Duration',
      dataIndex: '',
      visibility: true,
      render: (text, session) => (
        <>
          {`${
            moment(session?.END_TIME).diff(
              moment(session?.START_TIME),
              'minute',
            ) || 0
          } min`}
        </>
      ),
      sorter: (a, b) =>
        moment(a?.END_TIME).diff(moment(a?.START_TIME), 'minute', true) -
        moment(b?.END_TIME).diff(moment(b?.START_TIME), 'minute', true),
      width: 100,
    },
    {
      title: 'Unit Price',
      dataIndex: 'AVG_KWH_PRICE_WITHOUT_TAX',
      visibility: true,
      render: (text, session) => (
        <>
          {get(session, 'AVG_KWH_PRICE_WITHOUT_TAX')
            ? `₹${round(get(session, 'AVG_KWH_PRICE_WITHOUT_TAX'), 2)}`
            : '-'}
        </>
      ),
      key: 'AVG_KWH_PRICE_WITHOUT_TAX',
      sorter: (a, b) =>
        a?.AVG_KWH_PRICE_WITHOUT_TAX - b?.AVG_KWH_PRICE_WITHOUT_TAX,
      width: 100,
    },
    {
      title: 'Actual Energy',
      dataIndex: 'KWH_CONSUMED',
      visibility: true,
      render: (text, session) => (
        <>
          {get(session, 'KWH_CONSUMED')
            ? `${round(get(session, 'KWH_CONSUMED'), 2)} KWh`
            : '-'}
        </>
      ),
      key: 'KWH_CONSUMED',
      sorter: (a, b) => a?.KWH_CONSUMED - b?.KWH_CONSUMED,
      width: 100,
    },
    {
      title: 'Energy Requested',
      dataIndex: 'KWH_BILLED',
      visibility: true,
      render: (text, session) => (
        <>
          {get(session, 'KWH_BILLED')
            ? `${round(get(session, 'KWH_BILLED'), 2)} KWh`
            : '-'}
        </>
      ),
      key: 'KWH_BILLED',
      sorter: (a, b) => a?.KWH_BILLED - b?.KWH_BILLED,
      width: 100,
    },
    {
      title: 'Amount Consumed (excl. taxes)',
      dataIndex: 'AMOUNT_WITHOUT_TAX',
      visibility: true,
      render: (text, session) => (
        <>
          {get(session, 'AMOUNT_WITHOUT_TAX')
            ? `₹${get(session, 'AMOUNT_WITHOUT_TAX') / 100}`
            : '-'}
        </>
      ),
      key: 'AMOUNT_WITHOUT_TAX',
      sorter: (a, b) => a?.AMOUNT_WITHOUT_TAX - b?.AMOUNT_WITHOUT_TAX,
      width: 100,
    },
    {
      title: 'CGST',
      dataIndex: 'CGST',
      visibility: true,
      render: (text, session) => (
        <>{get(session, 'CGST') ? `₹${get(session, 'CGST') / 100}` : '-'}</>
      ),
      key: 'CGST',
      sorter: (a, b) => a?.CGST - b?.CGST,
      width: 70,
    },
    {
      title: 'SGST',
      dataIndex: 'SGST',
      visibility: true,
      render: (text, session) => (
        <>{get(session, 'SGST') ? `₹${get(session, 'SGST') / 100}` : '-'}</>
      ),
      key: 'SGST',
      sorter: (a, b) => a?.SGST - b?.SGST,
      width: 70,
    },
    {
      title: 'Total Amount (incl. taxes)',
      dataIndex: 'AMOUNT_CONSUMED',
      visibility: true,
      render: (text, session) => (
        <>
          {get(session, 'AMOUNT_CONSUMED')
            ? `₹${get(session, 'AMOUNT_CONSUMED') / 100}`
            : '-'}
        </>
      ),
      key: 'AMOUNT_CONSUMED',
      sorter: (a, b) => a?.AMOUNT_CONSUMED - b?.AMOUNT_CONSUMED,
      width: 100,
    },
    {
      title: 'Amount Billed (incl. taxes)',
      dataIndex: 'AMOUNT_BILLED',
      visibility: true,
      render: (text, session) => (
        <>
          {get(session, 'AMOUNT_BILLED')
            ? `₹${get(session, 'AMOUNT_BILLED') / 100}`
            : '-'}
        </>
      ),
      key: 'AMOUNT_BILLED',
      sorter: (a, b) => a?.AMOUNT_BILLED - b?.AMOUNT_BILLED,
      width: 100,
    },
    {
      title: 'Amount Refunded',
      dataIndex: 'AMOUNT_REFUNDED',
      visibility: true,
      render: (text, session) => (
        <>
          {get(session, 'AMOUNT_REFUNDED')
            ? `₹${get(session, 'AMOUNT_REFUNDED') / 100}`
            : '-'}
        </>
      ),
      key: 'AMOUNT_REFUNDED',
      sorter: (a, b) => a?.AMOUNT_REFUNDED - b?.AMOUNT_REFUNDED,
      width: 100,
    },
    {
      title: 'Amount Commission',
      dataIndex: 'AMOUNT_COMMISSION',
      visibility: true,
      render: (text, session) => (
        <>
          {get(session, 'AMOUNT_COMMISSION')
            ? `₹${round(get(session, 'AMOUNT_COMMISSION') / 100, 3)}`
            : ''}
          {get(session, 'AMOUNT_COMMISSION') &&
            getPopoverOfCommissionDetails(
              session?.COMMISSION_TYPE,
              session?.COMMISSION,
            )}
        </>
      ),
      key: 'AMOUNT_COMMISSION',
      sorter: (a, b) => a?.AMOUNT_COMMISSION - b?.AMOUNT_COMMISSION,
      width: 100,
    },

    {
      title: 'Transaction Status',
      dataIndex: '',
      visibility: true,
      render: (text, session) => (
        <>{`${session?.AMOUNT_CONSUMED ? 'Success' : 'Fail'}`}</>
      ),
      filters: [
        {
          text: 'Fail',
          value: 'Fail',
        },
        {
          text: 'Success',
          value: 'Success',
        },
      ],
      onFilter: (value, record) =>
        value === 'Success' ? record.AMOUNT_CONSUMED : !record.AMOUNT_CONSUMED,
      filterSearch: true,
      width: 100,
    },
    // {
    //   title: 'Initiated by',
    //   dataIndex: 'CUSTOMER_NAME',
    //   visibility: true,
    //   render: (text, session) => <>{get(session, 'CUSTOMER_NAME')}</>,
    //   key: 'REASON',
    //   width: 100,
    // },
  ];

  // useEffect(() => {
  //   if (queryString === '' && data?.length > 0) {
  //     setSessionData(data);
  //   }
  // }, [queryString, sessionData, data]);

  const [tableColumns, setTableColumns] = useState(columns);

  const dataLabelFormatter = (value) => {
    const formatter = Intl.NumberFormat('en-IN');
    const resp = `${formatter.format(round(value, 2))}`;
    return resp;
  };

  function makeData(sessions) {
    const formattedSessions = map(sessions, (session, s) => ({
      ...session,
      key: s,
    }));
    return formattedSessions;
  }

  const sumByKey = (key) => sumBy(sessionData, key);

  const getDurationTotal = (list) => {
    let durationSum = 0;
    if (list?.length > 0) {
      list.forEach((session) => {
        durationSum +=
          moment(session?.END_TIME).diff(
            moment(session?.START_TIME),
            'minute',
          ) || 0;
      });
    }
    return durationSum;
  };

  /**
   * Download CSV
   */
  const generateChargingSessionsCsvData = () => {
    if (!sessionData?.length > 0) return '';
    const meta = [
      {
        label: 'Charger Id',
        dataIndex: 'CPID',
        customValue: (row) => row?.CPID || '-',
      },
      { label: 'Transaction Id', dataIndex: 'TRANSACTIONID', customValue: '' },
      {
        label: 'Started at',
        dataIndex: 'START_TIME',
        customValue: (row) => localDate(row?.START_TIME)?.replace(',', ' '),
      },
      {
        label: 'Ended at',
        dataIndex: 'END_TIME',
        customValue: (row) => localDate(row?.END_TIME)?.replace(',', ' '),
      },
      {
        label: 'Duration',
        dataIndex: '',
        customValue: (row) =>
          moment(row?.END_TIME).diff(moment(row?.START_TIME), 'minute'),
      },
      {
        label: 'Unit Price',
        dataIndex: 'AVG_KWH_PRICE_WITHOUT_TAX',
        customValue: (row) =>
          row?.AVG_KWH_PRICE_WITHOUT_TAX
            ? `₹${round(row?.AVG_KWH_PRICE_WITHOUT_TAX, 2)}`
            : 0,
      },
      {
        label: 'Actual Energy',
        dataIndex: 'KWH_CONSUMED',
        customValue: (row) =>
          row?.KWH_CONSUMED ? `${round(row?.KWH_CONSUMED, 2)}KWh` : 0,
      },
      {
        label: 'Energy Requested',
        dataIndex: 'KWH_BILLED',
        customValue: (row) =>
          row?.KWH_BILLED ? `${round(row?.KWH_BILLED, 2)}KWh` : 0,
      },
      {
        label: 'Amount Consumed (excl. taxes)',
        dataIndex: 'AMOUNT_WITHOUT_TAX',
        customValue: (row) =>
          row?.AMOUNT_WITHOUT_TAX ? `₹${row?.AMOUNT_WITHOUT_TAX / 100}` : 0,
      },
      {
        label: 'CGST',
        dataIndex: 'CGST',
        customValue: (row) => (row?.CGST ? `₹${row?.CGST / 100}` : 0),
      },
      {
        label: 'SGST',
        dataIndex: 'SGST',
        customValue: (row) => (row?.SGST ? `₹${row?.SGST / 100}` : 0),
      },
      {
        label: 'Total Amount (incl. taxes)',
        dataIndex: 'AMOUNT_CONSUMED',
        customValue: (row) =>
          row?.AMOUNT_CONSUMED ? `₹${row?.AMOUNT_CONSUMED / 100}` : 0,
      },
      {
        label: 'Amount Billed (incl. taxes)',
        dataIndex: 'AMOUNT_BILLED',
        customValue: (row) =>
          row?.AMOUNT_BILLED ? `₹${row?.AMOUNT_BILLED / 100}` : 0,
      },
      {
        label: 'Amount Refunded',
        dataIndex: 'AMOUNT_REFUNDED',
        customValue: (row) =>
          row?.AMOUNT_REFUNDED ? `₹${row?.AMOUNT_REFUNDED / 100}` : 0,
      },
      {
        label: 'Amount Commission',
        dataIndex: 'AMOUNT_COMMISSION',
        customValue: (row) =>
          row?.AMOUNT_COMMISSION
            ? `₹${round(row?.AMOUNT_COMMISSION / 100, 3)}`
            : 0,
      },

      {
        label: 'Transaction Status',
        dataIndex: '',
        customValue: (row) => (row.AMOUNT_CONSUMED ? 'Success' : 'Fail'),
      },
      // {
      //   label: 'Initiated by',
      //   dataIndex: 'CUSTOMER_NAME',
      //   customValue: '',
      // },
    ];

    let filteredData = sessionData;
    if (filterObject && filterObject['15']) {
      const filterOnValue = filterObject['15'][0];
      if (filterOnValue) {
        filteredData = filter(sessionData, (d) =>
          filterOnValue === 'Success'
            ? d.AMOUNT_CONSUMED > 0
            : d.AMOUNT_CONSUMED <= 0,
        );
      }
    }
    let sortedData = filteredData;
    if (sortObject && sortObject.order) {
      sortedData = orderBy(
        filteredData,
        [sortObject.columnKey],
        [sortObject.order === 'ascend' ? 'asc' : 'desc'],
      );
    }

    let dataString = 'S.No,';
    meta.forEach((m) => {
      dataString += `${m.label},`;
    });
    dataString = dataString.slice(0, -1);
    dataString += '\n';

    sortedData.forEach((row, index) => {
      dataString += `${index + 1},`;
      meta.forEach((m) => {
        dataString += m.customValue
          ? m.customValue(row)
          : get(row, m.dataIndex);
        dataString += ',';
      });
      dataString = dataString.slice(0, -1);
      dataString += '\n';
    });

    const totalActualEnergy = sumByKey('KWH_CONSUMED');
    const totalEnergyRequested = sumByKey('KWH_BILLED');
    const totalAmountConsumedExTax = sumByKey('AMOUNT_WITHOUT_TAX');
    const totalAmountConsumed = sumByKey('AMOUNT_CONSUMED');
    const totalAmountBilled = sumByKey('AMOUNT_BILLED');
    const totalAmountRefunded = sumByKey('AMOUNT_REFUNDED');

    const totalAmountCommission = sumByKey('AMOUNT_COMMISSION');
    const totalDuration = getDurationTotal(sessionData);

    dataString += `,,,,Total,${totalDuration ? `${totalDuration} min` : 0},,${
      totalActualEnergy ? `${round(totalActualEnergy, 2)} KWh` : 0
    },${totalEnergyRequested ? `${round(totalEnergyRequested, 2)} KWh` : 0},${
      totalAmountConsumedExTax ? `₹${totalAmountConsumedExTax / 100}` : 0
    },,,${totalAmountConsumed ? `₹${totalAmountConsumed / 100}` : 0},${
      totalAmountBilled ? `₹${totalAmountBilled / 100}` : 0
    },${totalAmountRefunded ? `₹${totalAmountRefunded / 100}` : 0},${
      totalAmountCommission ? `₹${totalAmountCommission / 100}` : 0
    }`;
    return dataString;
  };

  const toggleColumnVisibility = (index) => {
    const prevTableColumns = cloneDeep(tableColumns);
    prevTableColumns[index].visibility = !tableColumns[index].visibility;
    setTableColumns(prevTableColumns);
  };

  const getContentForPopover = () =>
    tableColumns.map((c, i) => (
      /* eslint-disable jsx-a11y/no-static-element-interactions */
      <div
        key={c.title}
        className="pop-item"
        onClick={() => {
          toggleColumnVisibility(i);
        }}
        onKeyDown={() => {
          toggleColumnVisibility(i);
        }}
      >
        {c.visibility ? <EyeOutlined /> : <EyeInvisibleOutlined />}
        &nbsp;{c.title}
      </div>
    ));

  const getVisibilityPopover = () => (
    <Popover
      content={getContentForPopover()}
      title="Click to show/hide columns"
      trigger="click"
    >
      <Button
        // type="primary"
        size="large"
        style={{
          margin: '0 10px',
          backgroundColor: '#2675b8 !important',
          background: '#2675b8',
          color: 'white',
          fontSize: '14px',
          borderRadius: '5px',
        }}
        disabled={isLoading}
        onClick={() => {
          setVisibilityPopoverOpen(!isVisibilityPopoverOpen);
        }}
      >
        Show/Hide
      </Button>
    </Popover>
  );

  const getChargerDetails = (cpid) => {
    if (!cpid) return null;
    const charger = find(chargersDetails, { ID: cpid });

    return (
      <div>
        <div style={{ textAlign: 'center' }}>ChargerId: {charger?.ID}</div>
        <div style={{ fontSize: 12, color: '#8898aa' }}>{charger?.ADDRESS}</div>
      </div>
    );
  };

  const getSummary = (tableData) => {
    if (sessionData?.length === 0) return null;

    const totalDuration = getDurationTotal(sessionData);
    let totalActualEnergy = 0;
    let totalEnergyRequested = 0;
    let totalAmountConsumedExTax = 0;
    let totalAmountConsumed = 0;
    let totalAmountBilled = 0;
    let totalAmountRefunded = 0;
    let totalAmountCommission = 0;

    sessionData &&
      sessionData.forEach(
        ({
          KWH_CONSUMED,
          KWH_BILLED,
          AMOUNT_WITHOUT_TAX,
          AMOUNT_CONSUMED,
          AMOUNT_BILLED,
          AMOUNT_REFUNDED,
          AMOUNT_COMMISSION,
        }) => {
          totalActualEnergy += KWH_CONSUMED;
          totalEnergyRequested += KWH_BILLED;
          totalAmountConsumedExTax += AMOUNT_WITHOUT_TAX;
          totalAmountConsumed += AMOUNT_CONSUMED;
          totalAmountBilled += AMOUNT_BILLED;
          totalAmountRefunded += AMOUNT_REFUNDED;
          totalAmountCommission += AMOUNT_COMMISSION;
        },
      );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ fontWeight: '600' }}>
          <Table.Summary.Cell index={0} colSpan={4}>
            Total
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6}>
            <Text>{totalDuration ? `${totalDuration} min` : 0}</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={7} />
          <Table.Summary.Cell index={8}>
            <Text>
              {totalActualEnergy
                ? `${dataLabelFormatter(totalActualEnergy)} KWh`
                : 0}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={9}>
            <Text>
              {totalEnergyRequested
                ? `${dataLabelFormatter(totalEnergyRequested)} KWh`
                : 0}{' '}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={10}>
            <Text>
              {totalAmountConsumedExTax
                ? `₹${dataLabelFormatter(totalAmountConsumedExTax / 100)}`
                : 0}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={11} />
          <Table.Summary.Cell index={12} />
          <Table.Summary.Cell index={13}>
            <Text>
              {totalAmountConsumed
                ? `₹${dataLabelFormatter(totalAmountConsumed / 100)}`
                : 0}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={14}>
            <Text>
              {totalAmountBilled
                ? `₹${dataLabelFormatter(totalAmountBilled / 100)}`
                : 0}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={15}>
            <Text>
              {totalAmountRefunded
                ? `₹${dataLabelFormatter(totalAmountRefunded / 100)}`
                : 0}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={16}>
            <Text>
              {totalAmountCommission
                ? `₹${dataLabelFormatter(totalAmountCommission / 100)}`
                : 0}
            </Text>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const getTitle = () => {
    return (
      <div
        className="d-flex justify-content-space-between align-items-center"
        style={{ margin: '5px 0' }}
      >
        <div>
          {showDateRange && (
            <h3 className="mb-0">
              Charging Sessions{' '}
              <span style={{ fontSize: '12px', fontWeight: 'normal' }}>
                ({chargingSessionsParams?.startDate} -{' '}
                {chargingSessionsParams?.endDate})
              </span>
            </h3>
          )}
          <div style={{ marginRight: 10, fontWeight: 400 }}>
            (Total Sessions: {count})
          </div>
        </div>
        {chargingSessionsParams?.chargers?.length > 0 && (
          <div>{getChargerDetails(chargingSessionsParams?.chargers[0])}</div>
        )}
        <div className="d-flex justify-content-end align-items-center">
          {getVisibilityPopover()}
          <Button
            // type="primary"
            size="large"
            style={{
              margin: '0 10px 0 0',
              backgroundColor: '#2675b8 !important',
              background: '#2675b8',
              color: 'white',
              fontSize: '14px',
              borderRadius: '5px',
            }}
            disabled={isLoading}
            onClick={() => {
              setInvoicePopup(true);
            }}
          >
            Generate Invoice
          </Button>
          {sessionData && (
            <CSVLink
              data={generateChargingSessionsCsvData()}
              filename="ChargingSessions.csv"
              separator=","
              className="csv-button"
              style={{
                backgroundColor: '#2675b8',
                color: 'white',
                border: '0px solid transparent',
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '15px',
                paddingRight: '15px',
                borderRadius: '6px',
                marginBottom: '0px',
                height: '41px',
                marginRight: '10px',
                fontSize: '14px',
                fontWeight: 400,
                width: '150px',
              }}
            >
              Export as CSV
            </CSVLink>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Spin tip="Fetching Charging Sessions" spinning={isLoading}>
        {!isLoading && (
          <Table
            rowKey="key"
            className="table-striped-rows shadow"
            size="small"
            tableLayout="fixed"
            loading={isLoading}
            sscroll={{ y: 350, x: 'max-content' }}
            columns={filter(tableColumns, { visibility: true })}
            dataSource={makeData(sessionData)}
            // pagination={false}
            scroll={{ y: '40vh' }}
            style={{ border: '1px solid #e7e7e9', marginTop: 10 }}
            bordered
            title={() => getTitle()}
            summary={(tableData) => getSummary(tableData)}
            pagination={{
              // current: customerSessionsPageNum,
              defaultCurrent: 1,
              pageSize: limit,
              total: sessionData?.length,
              hideOnSinglePage: limit >= count,
              showSizeChanger: false,
            }}
            onChange={(pagination, filters, sorter) => {
              setSortObject(sorter);
              setFilterObject(filters);
            }}
          />
        )}
      </Spin>
      <ChargerSessionModal
        modalOpen={openChargerSessionModal}
        toggleModalOpen={toggleChargerSessionModal}
        transactionId={selectedSession}
      />
    </>
  );
};
export default ChargingSessionsTable;
